import { z } from 'zod';

/**
 * Codes d'erreur applicatifs. Le frontend s'appuie sur ces codes (jamais sur le
 * message) pour decider quoi afficher a l'utilisateur.
 */
export const ApiErrorCode = {
  BAD_REQUEST: 'BAD_REQUEST',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;

export type ApiErrorCodeId = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

/** Detail de validation associe a un champ precis d'un formulaire. */
export const fieldIssueSchema = z.object({
  /** Chemin du champ concerne, ex. « email » ou « profil.pseudo ». */
  path: z.string(),
  /** Message deja traduit en francais. */
  message: z.string(),
});

export type FieldIssue = z.infer<typeof fieldIssueSchema>;

/** Enveloppe unique de toutes les reponses d'erreur de l'API. */
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.enum(Object.values(ApiErrorCode) as [ApiErrorCodeId, ...ApiErrorCodeId[]]),
    message: z.string(),
    issues: z.array(fieldIssueSchema).optional(),
  }),
  /** Identifiant de la requete, a communiquer au support en cas de probleme. */
  requestId: z.string(),
});

export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

/** Correspondance code applicatif -> statut HTTP. */
export const HTTP_STATUS_BY_ERROR_CODE: Readonly<Record<ApiErrorCodeId, number>> = {
  BAD_REQUEST: 400,
  VALIDATION_ERROR: 422,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  RATE_LIMITED: 429,
  PAYLOAD_TOO_LARGE: 413,
  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

/** Messages par defaut, en francais, affichables tels quels. */
export const DEFAULT_ERROR_MESSAGES: Readonly<Record<ApiErrorCodeId, string>> = {
  BAD_REQUEST: 'La requete est invalide.',
  VALIDATION_ERROR: 'Certaines informations saisies sont invalides.',
  UNAUTHORIZED: 'Vous devez etre connecte pour effectuer cette action.',
  FORBIDDEN: "Vous n'avez pas les droits necessaires pour cette action.",
  NOT_FOUND: 'La ressource demandee est introuvable.',
  CONFLICT: 'Cette operation entre en conflit avec des donnees existantes.',
  RATE_LIMITED: 'Trop de requetes ont ete envoyees. Merci de patienter un instant.',
  PAYLOAD_TOO_LARGE: 'Les donnees envoyees sont trop volumineuses.',
  INTERNAL_ERROR: "Une erreur interne est survenue. L'incident a ete enregistre.",
  SERVICE_UNAVAILABLE: 'Le service est temporairement indisponible.',
};
